import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ALL_STEP_RUNS_BASE_KEY,
  LATEST_STEP_RUNS_KEY,
  LATEST_STEP_RUNS_BASE_KEY,
  RETRY_PRESENTATION_BASE_KEY,
  RUN_AUTO_EXPAND_PLAN_KEY,
  WAITING_STEP_DEPENDENCIES_BASE_KEY,
} from '@/utils/queryKeys.ts';
import { StepRun, StepsRuns } from '@/types/stepExecutions.ts';
import { SortingFunction } from '@/hooks/api/sse/runEvents/utils.ts';

// The run step-runs endpoint is now authoritative latest-snapshot data.
// We still normalize live SSE updates defensively because the stream is coalesced latest-state
// data rather than a complete ordered log of every intermediate transition.
const normalizeLatestStepRuns = (stepRuns: StepRun[], incomingStepRun: StepRun): StepRun[] => {
  const otherLatestStepRuns = stepRuns.filter(
    (existingStepRun) => existingStepRun.id !== incomingStepRun.id && existingStepRun.is_latest,
  );

  if (!incomingStepRun.is_latest) return otherLatestStepRuns;

  return [incomingStepRun];
};

const updateLatestStepRuns = (data: StepsRuns | undefined, stepRun: StepRun): StepsRuns | undefined => {
  const currentResults = data?.step_runs_by_step_id ?? {};

  if (stepRun.step_id in currentResults) {
    const old = currentResults[stepRun.step_id];
    const newStepRuns = normalizeLatestStepRuns(old, stepRun);
    const nextResults = { ...currentResults };
    if (newStepRuns.length > 0) {
      nextResults[stepRun.step_id] = [...newStepRuns].sort(SortingFunction);
    } else {
      delete nextResults[stepRun.step_id];
    }

    return {
      ...data,
      step_runs_by_step_id: nextResults,
    };
  }

  if (stepRun.is_latest) {
    return { ...data, step_runs_by_step_id: { ...currentResults, [stepRun.step_id]: [stepRun] } };
  }

  return data;
};

const useInvalidateStepRun = (automationId: string, runId: string) => {
  const queryClient = useQueryClient();

  return useCallback(
    (stepRun: StepRun): unknown[][] => {
      const scope = {
        parentPath: stepRun.parent_path,
        childIndex: stepRun.child_index,
      };
      const isRootScope = !scope.parentPath && scope.childIndex == null;
      const latestRootKey = LATEST_STEP_RUNS_KEY(automationId, runId);
      const latestScopePrefixKey = [
        ...LATEST_STEP_RUNS_BASE_KEY(automationId, runId),
        scope.parentPath ?? '',
        scope.childIndex ?? null,
      ];
      const allScopePrefixKey = [
        ...ALL_STEP_RUNS_BASE_KEY(automationId, runId),
        scope.parentPath ?? '',
        scope.childIndex ?? null,
      ];

      if (!isRootScope) {
        return [
          latestScopePrefixKey,
          allScopePrefixKey,
          RETRY_PRESENTATION_BASE_KEY(automationId, runId),
          WAITING_STEP_DEPENDENCIES_BASE_KEY(automationId, runId),
          RUN_AUTO_EXPAND_PLAN_KEY(automationId, runId),
        ];
      }

      const latestRootQuery = queryClient.getQueryCache().find({ queryKey: latestRootKey });
      const queryIsMountedWithData =
        latestRootQuery?.isActive() && latestRootQuery.state.data != null;
      if (!queryIsMountedWithData) {
        return [
          latestRootKey,
          allScopePrefixKey,
          RETRY_PRESENTATION_BASE_KEY(automationId, runId),
          WAITING_STEP_DEPENDENCIES_BASE_KEY(automationId, runId),
          RUN_AUTO_EXPAND_PLAN_KEY(automationId, runId),
        ];
      }

      queryClient.setQueryData<StepsRuns>(latestRootKey, (data) => updateLatestStepRuns(data, stepRun));

      return [
        allScopePrefixKey,
        RETRY_PRESENTATION_BASE_KEY(automationId, runId),
        WAITING_STEP_DEPENDENCIES_BASE_KEY(automationId, runId),
        RUN_AUTO_EXPAND_PLAN_KEY(automationId, runId),
      ];
    },
    [automationId, queryClient, runId],
  );
};

export default useInvalidateStepRun;
